import { STATSIG_EXPERIMENT_AB_KEYS, getStatsigUserConfig } from './statsigConfig'
import {
    getServerExperiment,
    getStatsigServer,
    initializeStatsig
} from './serverConfig'

import { STAGSIG_EXPERIMENT_VALUES, STATSIG_EXPERIMENT_NAMES_TYPE } from './type'
import { PAGE_NAME_TYPE } from '@/constants/pages/common'

/**
 * Server-side Statsig manager used by getHeadersForSSR (cookie-based
 * experiment propagation). Internally uses the @statsig/statsig-node-core
 * server SDK initialised at server bootstrap (see server/server.ts and
 * server/statsigServerConfig.ts).
 */
class StatsigManager {
    /**
     * Lazily ensure the underlying server SDK is initialised. Server bootstrap
     * already calls this in server.ts but we keep this defensive call so unit
     * tests / scripts that import StatsigManager still work.
     */
    static async initializeStastigInstance() {
        if (typeof window !== 'undefined') return
        if (!getStatsigServer()) {
            await initializeStatsig()
        }
    }

    /**
     * Resolve all experiments configured for a given page.
     * Returns an object keyed by experiment name with the resolved variant.
     */
    async getAllStatigAbValues(
        userId: string,
        pageName: PAGE_NAME_TYPE,
        customConfig?: Record<string, string | number | boolean | Array<string> | undefined>
    ) {
        if (!getStatsigServer()) {
            await initializeStatsig()
        }

        const abValues: Record<string, string | null> = {}
        const experimentPayload = STATSIG_EXPERIMENT_AB_KEYS[pageName]

        // userConfig kept around for parity with previous API in case future
        // custom-id mapping is added; current node-core SDK only consumes
        // stableID + custom map via StatsigUser.
        const userConfig = getStatsigUserConfig(userId, customConfig)

        for (const abValueKey of Object.keys(experimentPayload)) {
            const {
                experimentName = '',
                experimentValue = '',
                defaultValue
            } = experimentPayload[abValueKey as STATSIG_EXPERIMENT_NAMES_TYPE] || {}

            const value = getServerExperiment({
                userIdentifiers: {
                    stableID: userConfig.customIDs?.stableID,
                    ...(userConfig.custom as Record<string, string> | undefined)
                },
                experimentName,
                paramName: experimentValue,
                fallback: (defaultValue as string) ?? ''
            })

            abValues[abValueKey] = value ?? null
        }

        return abValues as STAGSIG_EXPERIMENT_VALUES
    }
}

export default StatsigManager
